// Config
// dependencies
import { List, Map, fromJS } from "immutable";
import { GenerateProcess } from "redux-process";

import Config from "./Config";
import InitialUI from "./UI";

import { DisplayConfirmationModal } from "../../actions/modals.actions";
// actions
import { ChangePage } from "../../actions/navigation.actions";
import { DisplayCheckbox, DisplaySuccess } from "../../actions/notifications.actions";
import { MergeStateChange, StateChange } from "../../actions/page.actions";
import { CancelUserUpdate, PendingUserUpdate, RedoLastSearch } from "../../actions/users.actions";

// selectors
import { getUserID } from "../../selectors/app";
import { getEditingGroupName, getGroupId, getGroupObject, getPageUISelector, getQueuedMemberList } from "./selectors";

export const changeScreen = (screen) => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["activeScreen"], screen));
};

export const makeGroupNameEditable = (isEditable) => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["isGroupNameEditable"], isEditable));
};

export const updateEditingGroupName = (value) => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["editingGroupName"], value));
};

export const changeEditableGroupName = (name) => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["editingGroupName"], name));
};

export const openAddMembersCurtain = () => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["openCurtain"], "addMembers"));
};

export const openAddAdminsCurtain = () => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["openCurtain"], "addAdmins"));
};

export const closeCurtain = () => (dispatch) => {
	dispatch(
		MergeStateChange(
			Config.pageName,
			[],
			Map({
				openCurtain: null,
				memberQueue: List(),
			})
		)
	);
};

export const getUserIsGroupAdmin = () => async (dispatch, getState) => {
	const state = getState();
	const groupID = getGroupId(state);
	const userID = getUserID(state);

	const result = await dispatch(GenerateProcess("GetUserIsGroupAdmin", { groupID, userID }));

	if (result.succeeded) {
		dispatch(StateChange(Config.pageName, ["userIsAGroupAdmin"], result.response));
	}
};

export const changePermission = (name, value) => async (dispatch, getState) => {
	const state = getState();
	const groupID = getGroupId(state);
	const payload = { [name]: value };

	const result = await dispatch(GenerateProcess("ChangeGroupPermission", { groupID, payload }));

	if (result.succeeded) {
		dispatch(StateChange(Config.pageName, ["permissions"], result.response));
		dispatch(DisplayCheckbox());
	}
};

export const addMemberToQueue = (member) => (dispatch, getState) => {
	/*
        the 'member' argument is a Map of the shape { email, id, name }
    */

	const state = getState();
	const memberQueue = getQueuedMemberList(state);

	dispatch(StateChange(Config.pageName, ["memberQueue"], memberQueue.push(member)));
};

export const removeMemberFromQueue = (employeeId) => (dispatch, getState) => {
	/*
        the 'member' argument is a Map of the shape { email, id, name }
    */

	const state = getState();
	const memberQueue = getQueuedMemberList(state);

	dispatch(
		StateChange(
			Config.pageName,
			["memberQueue"],
			memberQueue.filterNot((member) => member.get("id") === employeeId)
		)
	);
};

export const removeEmployeeFromGroup = (employeeID, membershipType) => async (dispatch, getState) => {
	/*
        removes an employee from a group. membershipType will determine if the
        process should remove a member or an admin.
    */

	if (["MEMBERS", "ADMINS"].indexOf(membershipType) < 0) {
		return;
	}

	const state = getState();
	// If MEMBERS, use new RemoveGroupMembers with async job
	if (membershipType === "MEMBERS") {
		const processName = "RemoveGroupMembers";
		const payload = {
			removeUsers: [employeeID],
			departmentID: getGroupId(state),
		};
		// Dispatch loader while request is processed.
		dispatch(PendingUserUpdate());
		const result = await dispatch(
			GenerateProcess(processName, {
				payload: payload,
			})
		);
		if (result.succeeded) {
			dispatch(RedoLastSearch());
			dispatch(DisplayCheckbox());
			dispatch(getUserIsGroupAdmin());
		} else {
			// Cancel loader
			dispatch(CancelUserUpdate());
		}
	} else {
		const processName = "RemoveGroupAdmins";
		const payload = {
			employeeIDs: [employeeID],
			departmentID: getGroupId(state),
		};
		const result = await dispatch(
			GenerateProcess(processName, {
				payload: payload,
			})
		);
		if (result.succeeded) {
			dispatch(RedoLastSearch());
			dispatch(DisplayCheckbox());
			dispatch(getUserIsGroupAdmin());
		}
	}
};

export const sendNewMembers = () => async (dispatch, getState) => {
	/*
        sends new members.
    */

	const state = getState();

	const result = await dispatch(
		GenerateProcess("AddGroupMembers", {
			payload: {
				departmentID: getGroupId(state),
				addUsers: getQueuedMemberList(state)
					.map((member) => member.get("id"))
					.toArray(),
			},
		})
	);

	if (result.succeeded) {
		dispatch(closeCurtain());
		dispatch(RedoLastSearch());
	}
};

export const sendNewAdmins = () => async (dispatch, getState) => {
	/*
        sends new admins.
    */

	const state = getState();

	const result = await dispatch(
		GenerateProcess("AddGroupAdmins", {
			payload: {
				departmentID: getGroupId(state),
				userIDs: getQueuedMemberList(state)
					.map((member) => member.get("id"))
					.toArray(),
			},
		})
	);

	if (result.succeeded) {
		dispatch(closeCurtain());
		dispatch(RedoLastSearch());
		dispatch(getUserIsGroupAdmin());
	}
};

export function fetchGroup() {
	return async (dispatch, getState) => {
		let state = getState();
		const groupId = getGroupId(state);

		const result = await dispatch(GenerateProcess("GetGroup", { groupId }));

		if (result.succeeded) {
			state = getState();
			const pageState = getPageUISelector(state);

			const nextPageState = pageState.withMutations((mutable) => {
				mutable.set("permissions", result.response.get("permissions"));
				mutable.set("editingGroupName", result.response.get("name", ""));
			});

			dispatch(StateChange(Config.pageName, [], nextPageState));
		}
	};
}

export function deleteGroup(shouldActuallyDelete) {
	return async (dispatch, getState) => {
		if (!shouldActuallyDelete) {
			dispatch(
				DisplayConfirmationModal("Are you sure you want to delete this group?", () => {
					dispatch(deleteGroup(true));
				})
			);

			return;
		}

		const state = getState();
		const groupID = getGroupId(state);

		const result = await dispatch(
			GenerateProcess("DeleteGroup", {
				payload: {
					groupID,
				},
			})
		);

		if (result.succeeded) {
			dispatch(DisplaySuccess("Deleted Group"));
			dispatch(ChangePage("/administration/groups/"));
		}
	};
}

export function sendNewGroupName() {
	return async (dispatch, getState) => {
		const state = getState();
		const groupId = getGroupId(state);
		const groupName = getEditingGroupName(state);

		const result = await dispatch(
			GenerateProcess("UpdateGroupName", {
				payload: {
					groupId,
					name: groupName,
				},
			})
		);

		if (result.succeeded) {
			dispatch(fetchGroup());
			dispatch(makeGroupNameEditable(false));
		}
	};
}

export const setColumns = (columns) => (dispatch) => {
	dispatch(StateChange(Config.pageName, ["employeesColumns"], fromJS(columns)));
};

export function initializeUI() {
	return (dispatch) => {
		dispatch(StateChange(Config.pageName, [], InitialUI));

		dispatch(getUserIsGroupAdmin());
		dispatch(fetchGroup());
	};
}
